/* BuilderTab — the Rule Builder, mounted inside RulesHub.
 *
 * 2×2 layout per the user's sketch:
 *
 *   ┌──────────────────┬─────────────────────────┐
 *   │ Ontology         │ Rules canvas            │
 *   │ (drag-drop)      │ (WHEN / IF / THEN / BC) │
 *   ├──────────────────┴─────────────────────────┤
 *   │ SPARQL preview   │ Test on {project} →     │
 *   │                  │ Promote                 │
 *   └──────────────────┴─────────────────────────┘
 *
 * The Ontology palette (Palette.tsx) auto-includes every non-abstract
 * NodeKind from `codegen.json` — that's how FloorPlan, Elevation,
 * Section, RCP, MEPPlan, Scene3D show up as draggable chips. No
 * separate Drawings tab needed (the drag surface IS the drawings).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles, FileText, Cpu, ListChecks, ArrowLeft, BookOpen, Eye } from "lucide-react";
import { Link } from "react-router-dom";
import { Palette } from "./builder/Palette";
import { Canvas } from "./builder/Canvas";
import { PreviewRail } from "./builder/PreviewRail";
import { DryRunStrip } from "./builder/DryRunStrip";
import { PromoteFooter } from "./builder/PromoteFooter";
import { SuggestionStrip } from "./builder/SuggestionStrip";
import { useActiveProjectGraph } from "../../lib/useActiveProjectGraph";
import { DESIGNER_RULES, type DesignerRule } from "../../data/designerRules";
import { designerRuleToCanvas } from "../../lib/ruleConvert";
import { validateRule } from "../../lib/ruleValidate";
import { ruleToSparql } from "../../lib/ruleToSparql";
import { promote, upsertCustom, type CustomRule } from "../../lib/projectRules";
import { createAgentBus } from "../../lib/agent/bus";
import { proposalsFromValidation } from "../../lib/agent/builderSuggestions";
import type { CanvasNode, Predicate, Action, RuleEvent } from "../../types/rule";

const SEED_NODES: CanvasNode[] = [
  { id: "n-when",    kind: "WHEN",    position: [0, 0], data: { kind: "WHEN", event: "onSave" } },
  {
    id: "n-if", kind: "IF", position: [0, 0],
    data: {
      kind: "IF",
      predicate: {
        kind: "cmp",
        lhs: { kind: "field", path: "SKU.leadDays", type: "number", unit: "days" },
        op: ">",
        rhs: { kind: "lit", type: "number", value: 60 },
      },
    },
  },
  { id: "n-then",    kind: "THEN",    position: [0, 0], data: { kind: "THEN", action: { kind: "flag", severity: "warn" } } },
  { id: "n-because", kind: "BECAUSE", position: [0, 0], data: { kind: "BECAUSE", template: "" } },
];

function pickPredicate(nodes: CanvasNode[]): Predicate {
  const ifn = nodes.find((n) => n.kind === "IF");
  if (ifn && ifn.data.kind === "IF") return ifn.data.predicate;
  return SEED_NODES[1]!.data.kind === "IF" ? (SEED_NODES[1]!.data as { kind: "IF"; predicate: Predicate }).predicate : ({ kind: "cmp", lhs: { kind: "lit", type: "number", value: 0 }, op: "==", rhs: { kind: "lit", type: "number", value: 0 } });
}
function pickAction(nodes: CanvasNode[]): Action {
  const tn = nodes.find((n) => n.kind === "THEN");
  if (tn && tn.data.kind === "THEN") return tn.data.action;
  return { kind: "flag", severity: "warn" };
}
function pickEvent(nodes: CanvasNode[]): RuleEvent {
  const wn = nodes.find((n) => n.kind === "WHEN");
  if (wn && wn.data.kind === "WHEN") return wn.data.event;
  return "onSave";
}
function pickBecause(nodes: CanvasNode[]): string {
  const bn = nodes.find((n) => n.kind === "BECAUSE");
  if (bn && bn.data.kind === "BECAUSE") return bn.data.template;
  return "";
}
function actionToVerb(a: Action): CustomRule["action"] {
  if (a.kind === "flag") return "flag";
  if (a.kind === "openApproval") return "openApproval";
  if (a.kind === "substitute") return "substitute";
  return "suggest";
}
function deriveTitle(predicate: Predicate, template: string): string {
  if (template && template.length > 8) return template.slice(0, 64);
  if (predicate.kind === "cmp") {
    const lhs = predicate.lhs.kind === "field" ? predicate.lhs.path : "value";
    const rhs = predicate.rhs.kind === "lit" ? predicate.rhs.value : "…";
    return `${lhs} ${predicate.op} ${rhs}`;
  }
  return "Custom rule";
}

interface BuilderTabProps {
  /** Tell the parent hub to switch back to Active rules after promote. */
  onPromoted?: () => void;
}

export function BuilderTab({ onPromoted }: BuilderTabProps): JSX.Element {
  const [search] = useSearchParams();
  const fromId = search.get("from");
  const nav = useNavigate();
  const { graph, activeId } = useActiveProjectGraph();

  const seed = useMemo<DesignerRule | null>(() => {
    if (!fromId) return null;
    return DESIGNER_RULES.find((r: DesignerRule) => r.id === fromId) ?? null;
  }, [fromId]);

  const initial = useMemo<CanvasNode[]>(() => {
    if (seed) return designerRuleToCanvas(seed);
    return SEED_NODES;
  }, [seed]);

  const [nodes, setNodes] = useState<CanvasNode[]>(initial);
  useEffect(() => { setNodes(initial); }, [initial]);

  const predicate = useMemo(() => pickPredicate(nodes), [nodes]);
  const validation = useMemo(() => validateRule(predicate), [predicate]);
  const [tested, setTested] = useState(false);

  // Agentic substrate (A.0) — translates validator findings into typed
  // ghost suggestions next to the dry-run output. Real surface, not a stub:
  // operates on the live predicate that the designer is editing.
  const busRef = useRef(createAgentBus());
  const ruleId = fromId ?? "draft";
  useEffect(() => {
    const bus = busRef.current;
    const nowIso = new Date().toISOString();
    const proposals = proposalsFromValidation(validation, { ruleId, nowIso });
    for (const p of proposals) bus.emit(p);
  }, [validation, ruleId]);

  const canPromote = validation.ok && tested;
  const reason = !validation.ok
    ? "Resolve validator errors before promoting."
    : !tested
      ? "Test the rule on this project first."
      : "All ontology-typed and tested — ready.";

  const onPromote = useCallback(() => {
    const id = fromId ?? `R-custom-${Date.now()}`;
    const action = pickAction(nodes);
    const trigger = pickEvent(nodes);
    const because = pickBecause(nodes);
    const sparql = ruleToSparql(predicate).query;
    const custom: CustomRule = {
      id,
      author: "user",
      createdAt: new Date().toISOString(),
      category: "spatial",
      title: deriveTitle(predicate, because),
      natural: because || deriveTitle(predicate, because),
      predicate,
      trigger,
      action: actionToVerb(action),
      severity: action.kind === "flag" ? action.severity : "warn",
      because: because || "—",
      sparql,
    };
    upsertCustom(activeId, custom);
    promote(activeId, id);
    if (onPromoted) onPromoted();
    else nav("/rules?tab=watching");
  }, [fromId, nodes, predicate, activeId, nav, onPromoted]);

  return (
    <div className="space-y-4 pb-32">
      {/* Context strip — tells the user where they came from and where this lands. */}
      <div className="panel p-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-[12px] text-ink-700">
          {seed ? (
            <>
              <Link to="/rules?tab=library" className="inline-flex items-center gap-1 text-ink-500 hover:text-ink-950">
                <ArrowLeft className="w-3 h-3" /> Library
              </Link>
              <span className="text-ink-300">/</span>
              <span className="text-ink-950 font-medium">{seed.title}</span>
              <span className="pill bg-cream-dark border border-border text-ink-500 text-[10px] tabular">tuning {seed.id}</span>
            </>
          ) : (
            <>
              <Sparkles className="w-3.5 h-3.5 text-foyr" />
              <span className="text-ink-950 font-medium">New rule from scratch</span>
              <span className="text-ink-500">— drag chips from the palette to begin.</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-1 text-[11px]">
          <Link to="/rules?tab=active"  className="inline-flex items-center gap-1 text-ink-500 hover:text-ink-950"><Eye      className="w-3 h-3" /> Active</Link>
          <span className="text-ink-300">·</span>
          <Link to="/rules?tab=library" className="inline-flex items-center gap-1 text-ink-500 hover:text-ink-950"><BookOpen className="w-3 h-3" /> Library</Link>
        </div>
      </div>

      <div className="panel p-4">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-3.5 h-3.5 text-foyr" />
          <span className="eyebrow text-foyr">How to compose a rule</span>
        </div>
        <p className="text-[13px] text-ink-700 leading-snug">
          Drag a <b className="text-ink-950">Thing</b> (Room, SKU, FloorPlan, …) or a <b className="text-ink-950">Field</b>
          {" "}(SKU.leadDays, Room.areaSqM, …) from the left into the canvas slot it belongs in.
          Comparators and aggregation chips drop on the middle slot. Every chip is a real ontology entry
          from <span className="font-mono">codegen.json</span> — what you see is what the agent reasons over.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-4 items-start">
        <Palette />
        <Canvas nodes={nodes} onChange={setNodes} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <FileText className="w-3.5 h-3.5 text-ink-500" />
            <span className="eyebrow">SPARQL · what the agent runs</span>
          </div>
          <PreviewRail predicate={predicate} />
        </div>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <ListChecks className="w-3.5 h-3.5 text-ink-500" />
            <span className="eyebrow">Test on this project · output</span>
          </div>
          <DryRunStrip
            predicate={predicate}
            projectName={graph.projectName}
            graphNodes={graph.nodes}
            disabled={!validation.ok}
            onResult={() => setTested(true)}
          />
          <SuggestionStrip bus={busRef.current} />
          <div className="panel p-4">
            <div className="flex items-center gap-2 mb-2">
              <Cpu className="w-3.5 h-3.5 text-ink-500" />
              <span className="eyebrow">What promote does</span>
            </div>
            <p className="text-[12px] text-ink-700 leading-snug">
              Writes the typed Rule into <span className="font-mono text-ink-950">{graph.projectName}</span>'s
              watchlist, and the agent's SPARQL evaluator starts firing it on every relevant event.
              Promote unlocks once validator is green and Test has run at least once.
            </p>
          </div>
        </div>
      </div>

      <PromoteFooter
        projectName={graph.projectName}
        canPromote={canPromote}
        reason={reason}
        onPromote={onPromote}
      />
    </div>
  );
}
